import { decodeEventLog } from 'viem'
import { facebookShareUrl, linkedinShareUrl, redditShareUrl, telegramShareUrl, whatsappShareUrl, xShareUrl } from 'views/LockTools/logic/certificate'

/**
 * Sharing a trading competition (Bez, 2026-10-03) and remembering a creation (no duplicate after success or reload). Pure, tested.
 * The link is the competition page; ?ref=<id of the sharer's /r/ link> makes it a referral link too (the page stores the inviter
 * first-touch, like /r/<id>). The card is /api/competitions/og/<id>.png.
 */
export const competitionPath = (id: number | string) => `/trading-competition/${id}`
export const competitionUrl = (origin: string, id: number | string, refId?: string | null) =>
  `${origin.replace(/\/$/, '')}${competitionPath(id)}${refId ? `?ref=${encodeURIComponent(refId)}` : ''}`
export const competitionOgUrl = (origin: string, id: number | string, scale?: 2) =>
  `${origin.replace(/\/$/, '')}/api/competitions/og/${id}.png${scale ? '?scale=2' : ''}`

const DAY = (t: number) => new Date(t * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })

export interface ShareFacts {
  id: number | string
  prize: string // "100,000,000 CBON"
  token: string // project token symbol
  pair?: string // "CBON/USDC"
  dex?: string // "CADINUdex V3"
  startTime: number
  endTime: number
  winners: number
}

/** "Trading competition #0 on CADINU: 100,000,000 CBON for the top 10. Trade CBON/USDC on CADINUdex V3, 3 Oct to 10 Oct (UTC)." */
export function shareMessage(f: ShareFacts): string {
  const where = f.pair ? `Trade ${f.pair}${f.dex ? ` on ${f.dex}` : ''}` : `Trade ${f.token}`
  return `Trading competition #${f.id} on CADINU: ${f.prize} for the top ${f.winners}. ${where}, ${DAY(f.startTime)} to ${DAY(f.endTime)} (UTC).`
}

export function competitionShareTargets(text: string, url: string) {
  return [
    { id: 'x', label: 'Post on X', href: xShareUrl(text, url) },
    { id: 'telegram', label: 'Telegram', href: telegramShareUrl(text, url) },
    { id: 'whatsapp', label: 'WhatsApp', href: whatsappShareUrl(text, url) },
    { id: 'facebook', label: 'Facebook', href: facebookShareUrl(url) },
    { id: 'linkedin', label: 'LinkedIn', href: linkedinShareUrl(url) },
    { id: 'reddit', label: 'Reddit', href: redditShareUrl(text, url) },
  ] as const
}

// ------------------------------------------------------------------ creation memory (no double submit)
export const LAUNCHED_ABI = [
  {
    type: 'event',
    name: 'NewCompetitionLaunched',
    anonymous: false,
    inputs: [
      { name: 'competitionId', type: 'uint256', indexed: true },
      { name: 'creator', type: 'address', indexed: true },
      { name: 'poolAddress', type: 'address', indexed: true },
      { name: 'startTime', type: 'uint256', indexed: false },
      { name: 'endTime', type: 'uint256', indexed: false },
      { name: 'rewardToken', type: 'address', indexed: false },
      { name: 'rewardAmount', type: 'uint256', indexed: false },
      { name: 'competitionTypeIndex', type: 'uint256', indexed: false },
      { name: 'rulesHash', type: 'bytes32', indexed: false },
      { name: 'validatorConfigId', type: 'uint256', indexed: false },
    ],
  },
] as const

/** The competition id from the creation receipt's logs (the V4 contract's NewCompetitionLaunched), or null. */
export function launchedIdFrom(logs: { address: string; data: `0x${string}`; topics: readonly `0x${string}`[] }[], contract: string): number | null {
  for (const l of logs) {
    if (l.address.toLowerCase() !== contract.toLowerCase()) continue
    try {
      const ev = decodeEventLog({ abi: LAUNCHED_ABI, data: l.data, topics: l.topics as [`0x${string}`, ...`0x${string}`[]] })
      const args = ev.args as unknown as { competitionId?: bigint }
      if (ev.eventName === 'NewCompetitionLaunched' && args.competitionId !== undefined) return Number(args.competitionId)
    } catch {
      /* another event */
    }
  }
  return null
}

export interface CreatedRecord {
  wallet: string
  hash: string
  id: number | null
  key: string // what was created (createKey)
  status: 'pending' | 'done'
  at: number // ms
  facts?: Omit<ShareFacts, 'id'>
}
export const CREATED_KEY = (wallet: string) => `cadinu.v4.created.${wallet.toLowerCase()}`

/** What a creation is, so the same one is never sent twice: pool, token, prize, winners, start, days, type. */
export function createKey(p: { pool: string; token: string; rewardToken: string; amount: string; winners: string | number; start: string; days: string | number; type: number }): string {
  return [p.pool, p.token, p.rewardToken].map((a) => a.toLowerCase()).concat([p.amount, String(p.winners), p.start, String(p.days), String(p.type)]).join('|')
}

/** On load: a creation of this wallet from the last 30 minutes (pending or done) is shown again instead of an empty form. */
export function restorable(r: CreatedRecord | null | undefined, wallet: string | undefined, now: number, keepMs = 30 * 60_000): CreatedRecord | null {
  if (!r || !wallet || r.wallet.toLowerCase() !== wallet.toLowerCase()) return null
  return now - r.at <= keepMs ? r : null
}

/** At submit: the same creation already sent by this wallet in the last 24 h blocks a second one. */
export function isDuplicate(r: CreatedRecord | null | undefined, wallet: string | undefined, key: string, now: number): boolean {
  return Boolean(r && wallet && r.wallet.toLowerCase() === wallet.toLowerCase() && r.key === key && now - r.at <= 24 * 3600_000)
}
